import React, { useState, useEffect, useContext, useMemo } from 'react';
import { RENTLE_SHOP_TERMS_URL, RENTLE_SHOP_PRIVACY_URL } from 'common/constants/urls';

import {
	TextField,
	Box,
	Button,
	Grid,
	Hidden,
	Typography,
	InputAdornment,
	IconButton,
	FormControl,
	SvgIcon,
	useMediaQuery,
	Tooltip,
	Link,
	Stack,
} from '@mui/material';
import { ReactComponent as CheckmarkCircleIcon } from 'common/assets/icons/icon-checkmark-circle-outline.svg';
import { useTheme } from '@mui/material/styles';

import RentleLogo from 'common/components/RentleLogo';
import CheckboxItem from 'common/components/CheckboxItem';
import RegisterBg from 'common/assets/register-bg.png';
import useFormField, { Form, validateForm } from 'common/hooks/useFormField';
import { isValidEmail } from 'common/utils/validation';

import Spinner from 'components/Spinner';

import { validatePassword } from 'services/utils';

import { Routes } from 'routing';
import i18n, { useTranslation, Trans } from 'services/localization';

import { useDispatch } from 'react-redux';
import * as UserActions from 'actions/UserActions';
import { getCtaContent } from 'utils/registerUtils';
import { BLACK_LISTED_COUNTRIES, CountryCode } from 'common/modules/atoms/countries';
import AutoCompleteCountryPicker from 'common/components/pickers/AutoCompleteCountryPicker';
import { Callable } from 'common/frontend/callable';
import { RegisterContext } from './RegisterContext';
import countryList from 'common/services/countryCodes.json';
import { createUserWithEmailAndPassword, User } from 'common/frontend/firebase/auth';
import { RiEyeFill, RiEyeOffFill, RiQuestionLine } from 'react-icons/ri';
import { logEvent } from 'common/services/analytics';
import { getAffiliateId } from './utils';

const validateEmail = (email: string) => {
	if (!email) {
		return i18n.t('common:validation.cannotBeEmpty', {
			field: i18n.t('common:form.emailAddress', 'Email address'),
			defaultValue: '{{field}} cannot be empty',
		}) as string;
	}

	if (!isValidEmail(email)) {
		return i18n.t('common:validation.isNotValidEmail');
	}
	return;
};

const validateShopName = (shopName: string) => {
	if (!shopName || shopName.length < 3) {
		return i18n.t('common:validation.lengthAtLeast', {
			field: i18n.t('register.shopName', 'Shop name'),
			int: '3',
			defaultValue: '{{field}} must be at least {{int}} characters',
		}) as string;
	}

	if (shopName.length > 100) {
		return i18n.t('common:validation.lengthAtMost', {
			shopName: i18n.t('register.shopName', 'Shop name'),
			int: '100',
			defaultValue: '{{shopName}} can be at most {{int}} characters',
		}) as string;
	}

	return;
};

const validateCountry = (countryCode: string) => {
	if (!countryCode) {
		return i18n.t('register.mustChooseCountry', 'You must choose a country');
	}

	if (BLACK_LISTED_COUNTRIES.includes(countryCode)) {
		return i18n.t('register.notAvailableInCountry', 'Rentle is not available in your country');
	}

	return;
};

const validateTerms = (termsAccepted: boolean) => {
	if (!termsAccepted) {
		return i18n.t(
			'register.mustAcceptTerms',
			'Terms of service must be accepted in order to use the service',
		);
	}

	return;
};

type RegistrationForm = {
	email: string;
	password: string;
	shopName: string;
	countryCode: string;
	termsAccepted: boolean;
};

const Register = () => {
	const dispatch = useDispatch();
	const context = useContext(RegisterContext);
	const [loading, setLoading] = useState<boolean>(false);
	const [countryLoaded, setCountryLoaded] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [isPasswordFocused, setIsPasswordFocused] = useState<boolean>(false);
	const theme = useTheme();

	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { t } = useTranslation();

	const passwordHelper = t('common:validation.lengthAtLeastIncludeLettersNumbers', {
		field: t('common:form.password', 'Password'),
		int: '7',
		defaultValue:
			'{{field}} must be at least {{int}} characters, and include both letters and numbers',
	}) as string;

	const form: Form<RegistrationForm> = {
		email: useFormField<string>('', validateEmail),
		password: useFormField<string>('', validatePassword),
		shopName: useFormField<string>('', validateShopName),
		countryCode: useFormField<string>('', validateCountry),
		termsAccepted: useFormField<boolean>(false, validateTerms),
	};

	const affiliateId = useMemo(() => getAffiliateId(), []);

	useEffect(() => {
		const getCountryFromIP = async () => {
			if (!!form.countryCode.value || countryLoaded) return;
			setCountryLoaded(true);
			try {
				const { country } = await Callable.users.getUserInfoFromIP();
				if (!!country && countryList.some((c) => c.code === country)) {
					form.countryCode.handleChange(country);
				}
			} catch (err) {}
		};
		getCountryFromIP();
	}, [form.countryCode, countryLoaded]);

	useEffect(() => {
		dispatch(UserActions.logout());
	}, [dispatch]);

	const immediateCountryValidation = (countryCode: string) => {
		if (BLACK_LISTED_COUNTRIES.includes(countryCode)) {
			return i18n.t('register.notAvailableInCountry', 'Rentle is not available in your country');
		}

		return;
	};

	const handleSubmit = async () => {
		const { errors, values } = validateForm(form);

		if (errors.length > 0) {
			return;
		}

		const { email, password, shopName, countryCode } = values;

		setLoading(true);

		let user: User | undefined;

		try {
			const registerResult = await createUserWithEmailAndPassword(email, password);
			user = registerResult.user;
		} catch (err) {
			form.email.setError(
				t('register.userWithEmailAlreadyExists', 'A user with that email address already exists'),
			);
			setLoading(false);
			return;
		}

		if (!user?.email) {
			setError(t('common:errors.somethingWentWrong', 'Something went wrong - please try again.'));
			setLoading(false);
			return;
		}

		context.handleRegister({
			uid: user.uid,
			email: user.email,
			shopName,
			countryCode: countryCode as CountryCode,
			affiliateId: affiliateId ?? undefined,
		});

		setLoading(false);
	};

	return (
		<Grid container sx={{ height: '100%' }}>
			<Grid item xs={12} lg={6} sx={{ px: 3 }}>
				<Box
					sx={{
						pb: 11,
						maxWidth: 355,
						margin: '0 auto',
						width: '100%',
						pt: {
							xs: 5.25,
							md: 8,
						},
					}}
				>
					<RentleLogo width={120} type="black" />
					<Box mt={isMobile ? 5.25 : 7} mb={3}>
						<Typography variant="h4" gutterBottom>
							{t('register.createFreeStore', 'Create your free Rentle store')}
						</Typography>
					</Box>
					<TextField
						variant="outlined"
						label={t('common:form.emailAddress', 'Email address')}
						placeholder={t('register.emailPlaceholder', 'john.doe@domain.com')}
						name="email"
						autoComplete="email"
						value={form.email.value}
						onChange={form.email.handleChange}
						onBlur={() => logEvent('register_action', { input: 'email' })}
						margin="none"
						type="text"
						error={Boolean(form.email.error)}
						helperText={form.email.error}
						fullWidth
						autoFocus
					/>
					<Box mt={2} />
					<TextField
						variant="outlined"
						label={t('common:form.password', 'Password')}
						placeholder={t('common:form.passwordPlaceholder', '')}
						name="password"
						autoComplete="new-password"
						value={form.password.value}
						onChange={form.password.handleChange}
						onFocus={() => setIsPasswordFocused(true)}
						onBlur={() => {
							setIsPasswordFocused(false);
							logEvent('register_action', { input: 'password' });
						}}
						margin="none"
						type={showPassword ? 'text' : 'password'}
						error={Boolean(form.password.error)}
						helperText={
							Boolean(form.password.error)
								? form.password.error
								: isPasswordFocused
								? passwordHelper
								: null
						}
						fullWidth
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowPassword(!showPassword)}
										edge="end"
										size="small"
									>
										{showPassword ? (
											<RiEyeOffFill color="#848484" />
										) : (
											<RiEyeFill color="#848484" />
										)}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Box mt={2} />
					<TextField
						variant="outlined"
						label={t('register.shopName', 'Shop name')}
						placeholder={t('register.shopNamePlaceholder', 'Rental Company Ltd.')}
						onBlur={() =>
							logEvent('register_action', { input: 'shop_name', data: form.shopName.value })
						}
						name="shopName"
						autoComplete="organization"
						value={form.shopName.value}
						onChange={form.shopName.handleChange}
						margin="none"
						type="text"
						error={Boolean(form.shopName.error)}
						helperText={form.shopName.error}
						fullWidth
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<Tooltip
										title={t(
											'register.shopNameHelper',
											"The name of your store which is shown to your customers. If you don't have a store name yet, you can put anything here. This can be changed later.",
										)}
										onFocus={() => logEvent('register_action', { input: 'shop_name_helper' })}
									>
										<IconButton aria-label="help" edge="end" size="small">
											<RiQuestionLine color="#848484" />
										</IconButton>
									</Tooltip>
								</InputAdornment>
							),
						}}
					/>
					<Box mt={2} />
					<FormControl fullWidth>
						<AutoCompleteCountryPicker
							label={t('register.countrySelectLabel', 'Pick your country')}
							id="user-country-select"
							value={form.countryCode.value}
							onChange={(country: string) => {
								form.countryCode.handleChange(country);
								logEvent('register_action', {
									input: 'country',
									data: country,
								});
							}}
							error={immediateCountryValidation(form.countryCode.value) || form.countryCode.error}
							variant="outlined"
							size="small"
						/>
					</FormControl>
					<Box mt={isMobile ? 3 : 5} />
					<CheckboxItem
						label={
							<Trans
								i18nKey="common:terms.acceptRentleTermsAndPrivacy"
								defaults="I have read and accept the Rentle <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>"
								components={{
									terms: (
										<Link
											sx={{
												color: (theme) => theme.palette.primary.main,
												textDecoration: 'none',
											}}
											href={RENTLE_SHOP_TERMS_URL}
											target="_blank"
											rel="noopener noreferrer"
										>
											{'Terms of Service'}
										</Link>
									),
									privacy: (
										<Link
											sx={{
												color: (theme) => theme.palette.primary.main,
												textDecoration: 'none',
											}}
											href={RENTLE_SHOP_PRIVACY_URL}
											target="_blank"
											rel="noopener noreferrer"
										>
											{'Privacy Policy'}
										</Link>
									),
								}}
							/>
						}
						checked={form.termsAccepted.value}
						onCheckedChange={(checked) => {
							form.termsAccepted.handleChange(checked);
							logEvent('register_action', {
								input: 'terms',
								data: checked,
							});
						}}
						error={form.termsAccepted.error}
						noPadding
						checkboxSize="small"
					/>
					<Box mt={isMobile ? 4 : 5} />
					<Button
						sx={{
							background: theme.palette.common.black,
							'&:hover': {
								background: (theme) => theme.palette.primary.dark,
							},
						}}
						disabled={loading}
						onClick={handleSubmit}
						variant="contained"
						fullWidth
					>
						{loading ? (
							<Spinner padding={8} thickness={4} color="white" />
						) : (
							t('register.createFreeAccount', 'Create free account')
						)}
					</Button>
					<Box mt={1} mb={isMobile ? 1 : 5}>
						{Boolean(error) && (
							<Typography variant="body2" color="error" align="center">
								{error}
							</Typography>
						)}
					</Box>
					<Typography align="center" variant="body2">
						{
							<Trans
								i18nKey="register.alreadyRegistered"
								defaults="Already using Rentle? <links>Sign in</links>"
								components={{
									links: (
										<Link
											sx={{
												color: (theme) => theme.palette.primary.main,
												textDecoration: 'none',
											}}
											href={Routes.LOGIN}
										>
											{'Sign in'}
										</Link>
									),
								}}
							/>
						}
					</Typography>
				</Box>
			</Grid>
			<Hidden lgDown>
				<Grid
					container
					spacing={0}
					item
					lg={6}
					justifyContent="center"
					alignItems="baseline"
					alignContent="flex-start"
					sx={{
						backgroundImage: `url("${RegisterBg}")`,
						backgroundRepeat: 'no-repeat',
						backgroundColor: theme.palette.common.black,
						backgroundPosition: 'right top',
						color: theme.palette.common.white,
						paddingTop: '160px',
						[theme.breakpoints.down('sm')]: {
							paddingTop: '130px',
						},
					}}
				>
					<Stack direction="column" spacing={15}>
						{getCtaContent().map(({ title, text }) => (
							<Stack direction="row" key={title} spacing={3}>
								<SvgIcon
									sx={{
										color: (theme) => theme.palette.common.white,
										fill: (theme) => theme.palette.common.white,
										fontSize: '4rem',
									}}
								>
									<CheckmarkCircleIcon />
								</SvgIcon>
								<Box>
									<Typography variant="h4" gutterBottom>
										{title}
									</Typography>
									<Typography variant="body1" fontWeight={300} maxWidth={350}>
										{text}
									</Typography>
								</Box>
							</Stack>
						))}
					</Stack>
				</Grid>
			</Hidden>
		</Grid>
	);
};

export default Register;
